import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useIsMobile } from '../../shared/hooks/useMediaQuery';

const STORAGE_KEY = 'shell:sidebar-collapsed';

/**
 * A boolean preference, no user data — safe in localStorage, and worth
 * persisting because a rail the user chose that springs back open on every
 * route change reads as a bug. Wrapped because Safari private mode and
 * "block site data" both make the accessor itself throw.
 */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    /* storage unavailable — the preference is simply not remembered */
  }
}

export interface SidebarState {
  /** Desktop rail mode. Always false on mobile, where the drawer is full width. */
  collapsed: boolean;
  toggleCollapsed: () => void;
  isMobile: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export function useSidebar(): SidebarState {
  // Below MEDIA.belowLg the sidebar stops being a column and becomes an
  // off-canvas drawer (plus, from Phase 171, a bottom tab bar).
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  const toggleCollapsed = useCallback((): void => {
    setCollapsed((prev) => {
      writeCollapsed(!prev);
      return !prev;
    });
  }, []);

  const openDrawer = useCallback((): void => {
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback((): void => {
    setDrawerOpen(false);
  }, []);

  // Following a link is the whole point of the drawer, so it must not survive
  // the navigation it caused. Keyed on pathname alone: a query-string change
  // (a table filter) is not a navigation the user made from the nav.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Rotating a phone to landscape, or dragging a window wider, crosses the
  // breakpoint with the drawer still open — it would otherwise stay mounted as
  // a modal on top of a layout that already shows the sidebar.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  return {
    // The rail is a desktop affordance; on mobile the drawer is always full width.
    collapsed: collapsed && !isMobile,
    toggleCollapsed,
    isMobile,
    drawerOpen,
    openDrawer,
    closeDrawer,
  };
}
